<template>
  <section class="pc-panel pc-panel--list">
    <header class="pc-panel__head">
      <span class="pc-panel__bar"></span>
      <h2 class="pc-panel__title">发票中心</h2>
    </header>

    <div class="pc-order-tabs">
      <el-tabs v-model="activeTab" @tab-click="onTabChange">
        <el-tab-pane
          v-for="tab in tabs"
          :key="tab.key"
          :name="tab.key"
        >
          <span slot="label" class="pc-tabs__label">
            {{ tab.label }}
            <span
              v-if="tabBadgeCount(tab.key) > 0"
              class="pc-tabs__badge"
            >
              {{ tabBadgeCount(tab.key) }}
            </span>
          </span>
        </el-tab-pane>
      </el-tabs>
    </div>

    <div v-if="loading" class="pc-empty pc-empty--placeholder">加载中...</div>
    <div
      v-else-if="isPendingTab && filteredOrders.length === 0"
      class="pc-empty pc-empty--placeholder"
    >
      <img class="pc-empty__img" :src="emptyPlaceholder" alt="" />
      <p class="pc-empty__text">暂无数据</p>
    </div>
    <div
      v-else-if="!isPendingTab && filteredInvoices.length === 0"
      class="pc-empty pc-empty--placeholder"
    >
      <img class="pc-empty__img" :src="emptyPlaceholder" alt="" />
      <p class="pc-empty__text">暂无数据</p>
    </div>

    <!-- 待开票：订单列表样式，与我的订单一致 -->
    <ul v-else-if="isPendingTab" class="pc-order-list indiv-scrollbar">
      <li
        v-for="order in filteredOrders"
        :key="order.orderNo || order.orderId"
        class="pc-order"
      >
        <div class="pc-order__top">
          <div class="pc-order__head-left">
            <span class="pc-order__status is-paid">已支付</span>
            <div class="pc-order__no-line">
              <span class="pc-order__no">{{ order.orderNo }}</span>
            </div>
          </div>
          <div class="pc-order__actions">
            <button
              type="button"
              class="pc-order__pay"
              @click="onApplyClick(order)"
            >
              申请开票
            </button>
            <button
              type="button"
              class="pc-order__detail"
              @click="toggleOrderDetail(order.orderNo)"
            >
              详情
              <i
                class="el-icon-arrow-down"
                :class="{ 'is-expanded': expandedNo === order.orderNo }"
              ></i>
            </button>
          </div>
        </div>
        <div class="pc-order__meta">
          <span class="pc-order__meta-item">
            <label>支付金额：</label>
            <em>￥{{ formatAmount(order.amount) }}</em>
          </span>
          <span class="pc-order__meta-item">
            <label>购买年限：</label>
            <b>{{ yearsLabel(order) }}</b>
          </span>
          <span class="pc-order__meta-item">
            <label>购买时间：</label>
            <b>{{ order.createTime || '—' }}</b>
          </span>
        </div>
        <el-collapse-transition>
          <div
            v-show="expandedNo === order.orderNo"
            class="pc-order__extra"
          >
            <div class="pc-order__meta pc-order__meta--extra">
              <span class="pc-order__meta-item">
                <label>订单类型：</label>
                <b>{{ order.orderTypeText || '—' }}</b>
              </span>
              <span class="pc-order__meta-item">
                <label>支付渠道：</label>
                <b>{{ payChannelText(order.payChannel) }}</b>
              </span>
            </div>
          </div>
        </el-collapse-transition>
      </li>
    </ul>

    <!-- 其他 tab：发票记录列表，复用订单卡片布局 -->
    <ul v-else class="pc-order-list indiv-scrollbar">
      <li
        v-for="item in filteredInvoices"
        :key="item.invoiceId"
        class="pc-order"
        :class="{ 'is-expired': isVoided(item) }"
      >
        <div class="pc-order__top">
          <div class="pc-order__head-left">
            <span
              class="pc-order__status"
              :class="invoiceStatusClass(item.status)"
            >
              {{ item.statusText || invoiceStatusText(item.status) }}
            </span>
            <div class="pc-order__no-line">
              <span class="pc-order__no">{{ orderNoOf(item) }}</span>
              <span
                v-if="isCancelRequested(item)"
                class="pc-order__cancel-badge"
                title="已申请作废"
              >
                <img
                  class="pc-order__cancel-badge-icon"
                  :src="cancelBadgeIcon"
                  alt=""
                />
                已申请作废
              </span>
            </div>
          </div>
          <div class="pc-order__actions">
            <button
              v-if="activeTab === 'issued' && item.invoiceFileUrl"
              type="button"
              class="pc-order__pay"
              @click="onDownloadInvoice(item)"
            >
              下载发票
            </button>
            <button
              v-if="canRequestCancel(item)"
              type="button"
              class="pc-order__detail"
              @click="openCancelDialog(item)"
            >
              申请作废
            </button>
            <button
              type="button"
              class="pc-order__detail"
              @click="toggleInvoiceDetail(item.invoiceId)"
            >
              详情
              <i
                class="el-icon-arrow-down"
                :class="{ 'is-expanded': isInvoiceExpanded(item.invoiceId) }"
              ></i>
            </button>
          </div>
        </div>
        <div class="pc-order__meta">
          <span
            v-if="item.rejectReason"
            class="pc-order__meta-item pc-order__meta-item--full pc-order__meta-item--reject"
          >
            <label>驳回原因：</label>
            <b class="is-reject">{{ item.rejectReason }}</b>
          </span>
          <span class="pc-order__meta-item">
            <label>开票金额：</label>
            <em>￥{{ formatAmount(item.amount) }}</em>
          </span>
          <span class="pc-order__meta-item">
            <label>发票抬头：</label>
            <b>{{ item.title || '—' }}</b>
          </span>
          <span class="pc-order__meta-item">
            <label>申请时间：</label>
            <b>{{ item.applyTime || '—' }}</b>
          </span>
        </div>
        <el-collapse-transition>
          <div
            v-show="isInvoiceExpanded(item.invoiceId)"
            class="pc-order__extra"
          >
            <div class="pc-order__meta pc-order__meta--extra">
              <span class="pc-order__meta-item">
                <label>发票种类：</label>
                <b>{{ item.invoiceCategoryText || invoiceCategoryText(item.invoiceCategory) }}</b>
              </span>
              <span class="pc-order__meta-item">
                <label>抬头类型：</label>
                <b>{{ item.invoiceTypeText || '—' }}</b>
              </span>
              <span class="pc-order__meta-item">
                <label>纳税人识别号：</label>
                <b>{{ item.taxNo || '—' }}</b>
              </span>
              <span class="pc-order__meta-item">
                <label>收票邮箱：</label>
                <b>{{ item.email || '—' }}</b>
              </span>
              <span v-if="item.processTime" class="pc-order__meta-item">
                <label>处理时间：</label>
                <b>{{ item.processTime }}</b>
              </span>
            </div>
          </div>
        </el-collapse-transition>
      </li>
    </ul>

    <ApplyInvoiceDialog
      :visible="applyDialogVisible"
      :order="applyOrder"
      @close="closeApplyDialog"
      @success="onApplySuccess"
    />

    <RequestCancelDialog
      :visible="cancelDialogVisible"
      :loading="cancelSubmitting"
      @close="closeCancelDialog"
      @confirm="onConfirmCancel"
    />
  </section>
</template>

<script>
/**
 * 发票中心：待开票 / 开票中 / 已开票 Tab，申请开票入口
 */
import { PAY_STATUS } from '../../config';
import { INVOICE_TABS, INVOICE_STATUS, ORDER_INVOICE_STATUS, invoiceCategoryText } from './constants';
import {
  fetchOrderList,
  fetchInvoiceList,
  checkInvoiceEligible,
  downloadInvoiceFile,
  invoiceFileNameFromUrl,
  requestInvoiceCancel,
} from './api';
import CollapseTransition from 'element-ui/lib/transitions/collapse-transition';
import ApplyInvoiceDialog from './ApplyInvoiceDialog.vue';
import RequestCancelDialog from './RequestCancelDialog.vue';
import emptyPlaceholder from '../../img/zw.png';
import cancelBadgeIcon from '../../img/attention.png';

const YEAR_LABELS = {
  1: '一年',
  2: '二年',
  3: '三年',
  4: '四年',
  5: '五年',
};

export default {
  name: 'PcInvoiceCenter',
  components: {
    ApplyInvoiceDialog,
    RequestCancelDialog,
    ElCollapseTransition: CollapseTransition,
  },
  data() {
    return {
      tabs: INVOICE_TABS,
      activeTab: 'pending',
      orders: [],
      invoices: [],
      loading: false,
      expandedNo: '',
      expandedInvoiceIds: [],
      applyDialogVisible: false,
      applyOrder: null,
      cancelDialogVisible: false,
      cancelInvoice: null,
      cancelSubmitting: false,
      PAY_STATUS,
      emptyPlaceholder,
      cancelBadgeIcon,
    };
  },
  computed: {
    currentTab() {
      return this.tabs.find((t) => t.key === this.activeTab) || this.tabs[0];
    },
    isPendingTab() {
      return this.currentTab.source === 'order';
    },
    /** 待开票：已支付且 invoiceStatus 为 0 的订单 */
    filteredOrders() {
      return this.orders.filter((order) => {
        if (Number(order.payStatus) !== PAY_STATUS.PAID) return false;
        return Number(order.invoiceStatus) === ORDER_INVOICE_STATUS.NONE;
      });
    },
    /** 按当前 tab 配置筛选发票记录 */
    filteredInvoices() {
      const statuses = this.currentTab.invoiceStatuses || [];
      if (!statuses.length) return [];
      return this.invoices.filter((item) =>
        statuses.includes(Number(item.status))
      );
    },
    /** 待开票数量 */
    pendingCount() {
      return this.filteredOrders.length;
    },
    /** 开票中数量（审核中 + 审核通过） */
    processingCount() {
      const tab = this.tabs.find((t) => t.key === 'processing');
      const statuses = (tab && tab.invoiceStatuses) || [];
      return this.invoices.filter((item) =>
        statuses.includes(Number(item.status))
      ).length;
    },
    /** orderId -> orderNo，用于发票列表展示订单号 */
    orderNoMap() {
      const map = {};
      this.orders.forEach((order) => {
        if (order.orderId != null) {
          map[order.orderId] = order.orderNo || String(order.orderId);
        }
      });
      return map;
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    invoiceCategoryText,
    /** tab 角标数量：待开票、开票中 */
    tabBadgeCount(key) {
      if (key === 'pending') return this.pendingCount;
      if (key === 'processing') return this.processingCount;
      return 0;
    },
    /** 切换 tab 时刷新列表并收起详情 */
    onTabChange() {
      this.expandedNo = '';
      this.expandedInvoiceIds = [];
      this.loadData();
    },
    /** 并行拉取订单与发票列表 */
    loadData() {
      this.loading = true;
      Promise.all([
        fetchOrderList(),
        fetchInvoiceList(),
      ])
        .then(([orders, invoices]) => {
          this.orders = orders || [];
          this.invoices = invoices || [];
          this.applyDefaultExpandedInvoices();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    /** 点击申请开票：先校验资格再打开弹窗 */
    onApplyClick(order) {
      if (!order || order.orderId == null) return;
      checkInvoiceEligible(order.orderId)
        .then((result) => {
          if (result && result.eligible === false) {
            this.$message.warning('该订单暂不可申请开票');
            return;
          }
          this.applyOrder = order;
          this.applyDialogVisible = true;
        });
    },
    closeApplyDialog() {
      this.applyDialogVisible = false;
      this.applyOrder = null;
    },
    onApplySuccess() {
      this.$message.success('申请成功');
      this.closeApplyDialog();
      this.loadData();
    },
    toggleOrderDetail(orderNo) {
      this.expandedNo = this.expandedNo === orderNo ? '' : orderNo;
    },
    toggleInvoiceDetail(invoiceId) {
      const idx = this.expandedInvoiceIds.indexOf(invoiceId);
      if (idx >= 0) {
        this.expandedInvoiceIds.splice(idx, 1);
      } else {
        this.expandedInvoiceIds.push(invoiceId);
      }
    },
    isInvoiceExpanded(invoiceId) {
      return this.expandedInvoiceIds.includes(invoiceId);
    },
    /** 已驳回 / 已作废 tab：默认展开全部详情 */
    applyDefaultExpandedInvoices() {
      const defaultExpandStatusByTab = {
        rejected: INVOICE_STATUS.REJECTED,
        voided: INVOICE_STATUS.VOIDED,
      };
      const status = defaultExpandStatusByTab[this.activeTab];
      if (status == null) return;
      this.expandedInvoiceIds = this.filteredInvoices
        .filter((item) => Number(item.status) === status)
        .map((item) => item.invoiceId)
        .filter((id) => id != null);
    },
    /** 拉取 PDF 后触发下载，避免浏览器直接打开 */
    onDownloadInvoice(item) {
      const url = item && item.invoiceFileUrl;
      if (!url) return;
      const fullUrl = `${window.location.origin}/api${url}`;
      downloadInvoiceFile(fullUrl, invoiceFileNameFromUrl(url))
        .catch((err) => {
          this.$message.error((err && err.message) || '发票下载失败');
        });
    },
    /** 已开票且未申请作废时可申请 */
    canRequestCancel(item) {
      if (this.activeTab !== 'issued') return false;
      if (!item || item.invoiceId == null) return false;
      return Number(item.status) === INVOICE_STATUS.ISSUED;
    },
    isCancelRequested(item) {
      return Number(item && item.status) === INVOICE_STATUS.CANCEL_REQUESTED;
    },
    openCancelDialog(item) {
      if (!this.canRequestCancel(item)) return;
      this.cancelInvoice = item;
      this.cancelDialogVisible = true;
    },
    closeCancelDialog() {
      if (this.cancelSubmitting) return;
      this.cancelDialogVisible = false;
      this.cancelInvoice = null;
    },
    onConfirmCancel() {
      const item = this.cancelInvoice;
      if (!item || item.invoiceId == null || this.cancelSubmitting) return;
      this.cancelSubmitting = true;
      requestInvoiceCancel(item.invoiceId)
        .then(() => {
          this.$message.success('申请成功');
          this.cancelDialogVisible = false;
          this.cancelInvoice = null;
          this.loadData();
        })
        .catch((err) => {
          this.$message.error((err && err.message) || '申请失败');
        })
        .finally(() => {
          this.cancelSubmitting = false;
        });
    },
    orderNoOf(invoice) {
      if (!invoice) return '—';
      return this.orderNoMap[invoice.orderId] || String(invoice.orderId || '—');
    },
    formatAmount(amount) {
      if (amount == null || amount === '') return '0.00';
      return String(amount);
    },
    yearsLabel(order) {
      const years = Number(order.years);
      if (!years) return '—';
      return YEAR_LABELS[years] || `${years}年`;
    },
    payChannelText(channel) {
      const map = {
        alipay: '支付宝',
        wxpay: '微信支付',
      };
      return map[channel] || channel || '—';
    },
    /** 已作废发票 */
    isVoided(item) {
      return Number(item.status) === INVOICE_STATUS.VOIDED;
    },
    invoiceStatusText(status) {
      const map = {
        [INVOICE_STATUS.PENDING_REVIEW]: '审核中',
        [INVOICE_STATUS.APPROVED]: '审核通过',
        [INVOICE_STATUS.REJECTED]: '审核驳回',
        [INVOICE_STATUS.ISSUED]: '已开票',
        [INVOICE_STATUS.VOIDED]: '已作废',
        [INVOICE_STATUS.CANCEL_REQUESTED]: '已开票',
      };
      return map[Number(status)] || '—';
    },
    /** 发票状态标签颜色 */
    invoiceStatusClass(status) {
      const s = Number(status);
      if (s === INVOICE_STATUS.PENDING_REVIEW) return 'is-processing';
      if (s === INVOICE_STATUS.APPROVED) return 'is-paid';
      if (s === INVOICE_STATUS.ISSUED) return 'is-paid';
      if (s === INVOICE_STATUS.CANCEL_REQUESTED) return 'is-paid';
      if (s === INVOICE_STATUS.REJECTED) return 'is-pending';
      return 'is-muted';
    },
  },
};
</script>

<style scoped src="./style.css"></style>



// WEBPACK FOOTER //
// src/view/client/personal-center/panels/InvoiceCenter/index.vue